import { useToast } from '@airship-platform/airship-patterns';
import { DiscountAndFee } from '@airship-platform/airship-proposal';
import {
  BaseDialog,
  FlexBox,
  ItemSelector,
} from '@airship-platform/jam-web/components';
import { PlusIcon } from '@airship-platform/jam-web/icons';
import { IconButton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useAppointmentProposalOptionModifiers } from '../../../data-access/appointmentProposalOptionModifiers';
import { useAppointmentProposalOptions } from '../../../data-access/appointmentProposalOptions';
import { getSelectedOptionNames } from '../../proposal-options';

interface AddDiscountButtonProps {
  discount: DiscountAndFee;
}

const AddDiscountButton: React.FC<AddDiscountButtonProps> = ({ discount }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const { options, currentOption } = useAppointmentProposalOptions();
  const {
    getNextSortOrder,
    createManyAppointmentProposalOptionModifiers,
    isLoadingModifierIds,
  } = useAppointmentProposalOptionModifiers();
  const { showToast } = useToast();

  useEffect(() => {
    if (currentOption?.appointmentProposalOptionId) {
      setSelectedOptions([currentOption.appointmentProposalOptionId]);
    }
  }, [currentOption]);

  if (!currentOption) {
    return null;
  }

  const handleOptionCheckboxChange = (optionId: string) =>
    setSelectedOptions((prev) =>
      prev.includes(optionId)
        ? prev.filter((id) => id !== optionId)
        : [...prev, optionId]
    );

  const onAddDiscountClick = async () => {
    const created = await createManyAppointmentProposalOptionModifiers(
      currentOption.appointmentId,
      currentOption.appointmentProposalId,
      selectedOptions.map((appointmentProposalOptionId, idx) => ({
        appointmentProposalOptionId,
        modifier: {
          jobId: currentOption.jobId,
          discountsAndFeesId: discount.discountsAndFeesId,
          modifierType: discount.modifierType,
          displayName: discount.displayName,
          description: discount.description,
          amountType: discount.amountType,
          amount: discount.amount,
          limit: discount.limit,
          taxable: discount.taxable,
          categoryIds: discount.categoryIds,
          images: discount.images,
          roundingRule: discount.roundingRule,
          taxBasis: discount.taxBasis,
          qualifyingPricebookCategoryIds:
            discount.qualifyingPricebookCategoryIds,
          qualifyingPricebookIds: discount.qualifyingPricebookIds,
          sortOrder: getNextSortOrder(idx),
        },
      }))
    );

    if (!created.length) {
      return;
    }

    const optionNames = getSelectedOptionNames(options, selectedOptions);
    showToast(
      `${discount.displayName} added to ${optionNames.join(', ')}`,
      'success'
    );
    setIsModalOpen(false);
  };

  return (
    <>
      <IconButton color="softGold" onClick={() => setIsModalOpen(true)}>
        <PlusIcon />
      </IconButton>
      <BaseDialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Add Discount"
        footerAction={{
          left: {
            children: 'Cancel',
            onClick: () => setIsModalOpen(false),
            color: 'softGold',
          },
          right: {
            children: 'Add',
            onClick: onAddDiscountClick,
            disabled:
              !selectedOptions.length ||
              isLoadingModifierIds.includes(discount.discountsAndFeesId),
          },
        }}
      >
        <FlexBox flexDirection={'column'} padding={2} gap={2}>
          <Typography variant="subtitle2">{discount.displayName}</Typography>
          <Typography variant="body2">{discount.description}</Typography>

          <Typography variant={'subtitle2'}>Add to</Typography>
          <FlexBox gap={2} flexWrap={'wrap'}>
            {options.map(
              (option) =>
                option.appointmentProposalOptionId && (
                  <ItemSelector
                    key={option.appointmentProposalOptionId}
                    label={option.name}
                    value={option.name}
                    checked={selectedOptions.includes(
                      option.appointmentProposalOptionId
                    )}
                    disabled={option.isReadOnly}
                    type="checkbox"
                    handleChange={() =>
                      handleOptionCheckboxChange(
                        option.appointmentProposalOptionId
                      )
                    }
                  />
                )
            )}
          </FlexBox>
        </FlexBox>
      </BaseDialog>
    </>
  );
};

export default AddDiscountButton;
